import { lighten, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { MemberTitle } from '../models/member';

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => <Tooltip {...props} classes={{ popper: className }} />)(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#fff',
    color: 'rgba(0, 0, 0, 0.87)',
    lineHeight: 1,
    maxWidth: 220,
    padding: '0.5rem 1rem',
    border: '1px solid #dadde9',
    boxShadow: '1px 0 5px rgba(0,0,0,0.3)',
  },
}));

type MemberTitleBadgeProps = {
  memberTitle: MemberTitle;
};

export const MemberTitleBadge = (props: MemberTitleBadgeProps) => {
  const { memberTitle } = props;
  const { t } = useTranslation(['member']);

  const isResigned = memberTitle.resdate && dayjs(memberTitle.resdate).isBefore(new Date());
  return (
    <HtmlTooltip
      title={
        <>
          {memberTitle.title && (
            <>
              <Typography variant='caption'>{t('member:memberDetails.memberTitle.title')}</Typography>
              <Typography gutterBottom>{memberTitle.title}</Typography>
            </>
          )}
          {memberTitle.organizationName && (
            <>
              <Typography variant='caption'>{t('member:memberDetails.memberTitle.organizationName')}</Typography>
              <Typography gutterBottom>{memberTitle.organizationName}</Typography>
            </>
          )}

          {memberTitle.admdate && (
            <>
              <Typography variant='caption'>{t('member:memberDetails.memberTitle.admdate')}</Typography>
              <Typography gutterBottom>{memberTitle.admdate}</Typography>
            </>
          )}

          {memberTitle.resdate && (
            <>
              <Typography variant='caption'>{t('member:memberDetails.memberTitle.resdate')}</Typography>
              <Typography gutterBottom>{memberTitle.resdate}</Typography>
            </>
          )}
        </>
      }
    >
      <Typography
        variant='body2'
        sx={{
          position: 'relative',
          fontSize: '0.8rem',
          backgroundColor: (thm) => (isResigned ? thm.palette.grey[200] : lighten(thm.palette.success.main, 0.9)),
          color: (thm) => (isResigned ? thm.palette.text.secondary : thm.palette.text.primary),
          boxSizing: 'border-box',
          borderRadius: 1,
          pl: 2.75,
          pr: 1,
          py: 0.25,
          mr: 0.5,
          mb: 0.5,
          '&::after': {
            position: 'absolute',
            content: '" "',
            top: '50%',
            left: (thm) => thm.spacing(1),
            mt: '-4px',
            backgroundColor: (thm) => (isResigned ? thm.palette.grey[100] : thm.palette.success.main),
            border: (thm) => (isResigned ? `1px solid ${thm.palette.grey[500]}` : 'none'),
            width: '8px',
            height: '8px',
            borderRadius: '500rem',
          },
        }}
      >
        {memberTitle.title}
      </Typography>
    </HtmlTooltip>
  );
};
